import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, ActionPerformed } from '@capacitor/push-notifications';
import { Gleap } from 'capacitor-gleap-plugin';
import { FCM } from "@capacitor-community/fcm";
import { PushDevicesService } from './push-devices.service';
import { PushDeeplinkResolverService } from './push-deeplink-resolver.service';

@Injectable({
	providedIn: 'root'
})
export class NotificationsService {
	private fcmToken: string | null = null;
	// Single-slot queue: a cold-start tap that fires before this service is
	// "ready" (i.e. before initPushNotifications() finishes its setup) gets
	// stored here and replayed once by flushPendingDeeplink(). A newer tap (or
	// this queue being drained) always wins over a stale one — see design D4.
	private pendingDeeplink: string[] | null = null;
	private deeplinkReady = false;

	constructor(
		private router: Router,
		private zone: NgZone,
		private pushDevices: PushDevicesService,
		private deeplinkResolver: PushDeeplinkResolverService
	) { }

	public async initPushNotifications() {
		if (Capacitor.getPlatform() !== 'web') {
			await this.registerPushNotifications();
			await this.setupFCM();
		} else {
			console.warn('Push notifications only available on device');
		}
		// initPushNotifications() only ever runs on an already-authenticated
		// path (boot-with-token, post-login), so its completion IS the
		// auth/route-tree-ready signal — see design D4.
		this.flushPendingDeeplink();
	}

	private async registerPushNotifications() {
		try {
			// Solicitar permisos
			const permission = await PushNotifications.requestPermissions();
			if (permission.receive === 'granted') {
				// Registrar para push notifications
				await PushNotifications.register();
				console.log('Push notification registration successful');
			} else {
				console.warn('Push notification permission denied');
				return;
			}

			// Listener para token de registro
			PushNotifications.addListener('registration', (token) => this.onRegistrationToken(token));

			// Listener para errores de registro
			PushNotifications.addListener('registrationError', (error) => {
				console.error('Registration error: ', error);
			});

			// Listener para notificaciones recibidas (app en foreground)
			PushNotifications.addListener('pushNotificationReceived', (notification) => {
				console.log('Push notification received: ', notification);
				// Opcional: Mostrar alerta local o actualizar UI
			});

			// Listener para cuando se toca la notificación
			PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
				console.log('Push notification action performed', notification);
				this.handleNotificationAction(notification);
			});

		} catch (error) {
			console.error('Error setting up push notifications:', error);
		}
	}

	private async onRegistrationToken(token: { value: string }): Promise<void> {
		console.log('Registration token: ', token.value);
		this.fcmToken = token.value;
		await this.pushDevices.registerDevice(token.value);
	}

	private async setupFCM() {
		try {
			// Obtener token FCM
			const fcmResult = await FCM.getToken();
			await this.onFcmToken(fcmResult.token);

			// Configurar callbacks de Gleap para suscripciones a topics
			Gleap.setEventCallback((message: any) => {
				const { name, data } = message;
				if (name === "register-pushmessage-group") {
					FCM.subscribeTo({ topic: data });
					console.log(`Subscribed to topic: ${data}`);
				}
				if (name === "unregister-pushmessage-group") {
					FCM.unsubscribeFrom({ topic: data });
					console.log(`Unsubscribed from topic: ${data}`);
				}
			});

		} catch (error) {
			console.error('Error setting up FCM:', error);
		}
	}

	private async onFcmToken(token: string | null | undefined): Promise<void> {
		if (!token) {
			return;
		}
		this.fcmToken = token;
		console.log('FCM Token: ', token);
		await this.pushDevices.registerDevice(token);
	}

	private handleNotificationAction(action: ActionPerformed): void {
		const commands = this.deeplinkResolver.resolve(action.notification?.data);
		if (!commands) {
			console.warn('Push notification action: no route resolved for payload', action.notification?.data);
			return;
		}
		this.navigateOrQueue(commands);
	}

	private navigateOrQueue(commands: string[]): void {
		// A newer tap always supersedes whatever was previously queued —
		// single slot, latest wins (design D4).
		this.pendingDeeplink = null;
		if (this.deeplinkReady) {
			this.zone.run(() => this.router.navigate(commands));
		} else {
			this.pendingDeeplink = commands;
		}
	}

	// Called once, at the end of initPushNotifications(). Marks the service
	// ready and replays whatever deeplink is currently in the single slot
	// (if any) — a stale tap superseded by a newer one before this point was
	// already discarded by navigateOrQueue(), so at most one navigation fires.
	public flushPendingDeeplink(): void {
		this.deeplinkReady = true;
		const commands = this.pendingDeeplink;
		this.pendingDeeplink = null;
		if (commands) {
			this.zone.run(() => this.router.navigate(commands));
		}
	}

	// Método público para obtener el token actual
	public getCurrentToken(): string | null {
		return this.fcmToken;
	}

	// Método para suscribirse manualmente a un topic
	public async subscribeToTopic(topic: string) {
		try {
			await FCM.subscribeTo({ topic });
			console.log(`Subscribed to topic: ${topic}`);
		} catch (error) {
			console.error(`Error subscribing to topic ${topic}:`, error);
		}
	}

	// Método para desuscribirse de un topic
	public async unsubscribeFromTopic(topic: string) {
		try {
			await FCM.unsubscribeFrom({ topic });
			console.log(`Unsubscribed from topic: ${topic}`);
		} catch (error) {
			console.error(`Error unsubscribing from topic ${topic}:`, error);
		}
	}
}
